/**
 * Shared sidebar style constants for styled-components.
 * Matches legacy Contently _sidenav.sass exactly.
 *
 * Usage:
 *   import { sidebarLink, sidebarLinkActive, sidebarChildLink, sidebarChildLinkActive, sidebarHeader } from '../../styles/sidebarStyles';
 *   const NavLink = styled.a<{ $active?: boolean }>`${props => props.$active ? sidebarLinkActive : sidebarLink}`;
 */
import { css } from 'styled-components';

/* -- Color tokens (from legacy _variables.scss / _sidenav.sass) -- */
export const sidebarColors = {
  /** Inactive link text — legacy $gray */
  inactive: '#666',
  /** Active link text — legacy $blue-primary-dark */
  active: '#0094ab',
  /** Hover background */
  hoverBg: '#f0f0f0',
  /** Section header text */
  header: '#666',
  /** Active left-border indicator */
  activeBorder: '#0094ab',
  /** Badge background */
  badge: '#0094ab',
} as const;

/* -- Shared transition -- */
const linkTransition = css`
  transition: all 0.3s ease;
`;

/* -- Top-level sidebar link (inactive) -- */
export const sidebarLink = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px;
  font-size: 14px;
  color: ${sidebarColors.inactive};
  font-weight: 400;
  text-decoration: none;
  cursor: pointer;
  ${linkTransition}

  &:hover {
    background: ${sidebarColors.hoverBg};
    color: ${sidebarColors.active};
  }
`;

/* -- Top-level sidebar link (active) -- */
export const sidebarLinkActive = css`
  ${sidebarLink}
  color: ${sidebarColors.active};
  font-weight: 500;
  border-left: 3px solid ${sidebarColors.activeBorder};
  padding-left: 17px;
`;

/* -- Child sidebar link (inactive) -- */
export const sidebarChildLink = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 20px 6px 36px;
  font-size: 12px;
  color: ${sidebarColors.inactive};
  font-weight: 400;
  text-decoration: none;
  cursor: pointer;
  ${linkTransition}

  &:hover {
    background: ${sidebarColors.hoverBg};
    color: ${sidebarColors.active};
  }
`;

/* -- Child sidebar link (active) -- */
export const sidebarChildLinkActive = css`
  ${sidebarChildLink}
  color: ${sidebarColors.active};
  font-weight: 500;
`;

/* -- Group header label (e.g. "Templates") — matches legacy: same size as links, medium weight, #333 -- */
export const sidebarHeaderStyle = css`
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

/* -- Badge (e.g. "NEW") -- */
export const sidebarBadge = css`
  display: inline-block;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  color: white;
  background: ${sidebarColors.badge};
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 8px;
`;
